/** Page de gestion des sites - CRUD complet */

import { useState } from 'react';
import { Building2, Pencil, Plus, Radio, Trash2, Users } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import {
  useCreateSite,
  useDeleteSite,
  useSites,
  useUpdateSite,
  type Site,
  type SitePayload,
} from '../hooks/useSites';

export function SitesPage() {
  const { data: sites, isLoading } = useSites();
  const createMut = useCreateSite();
  const updateMut = useUpdateSite();
  const deleteMut = useDeleteSite();

  const [editing, setEditing] = useState<Site | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<SitePayload>({ name: '', address: '' });
  const [error, setError] = useState('');

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '' });
    setError('');
    setOpen(true);
  };

  const openEdit = (site: Site) => {
    setEditing(site);
    setForm({ name: site.name, address: site.address ?? '' });
    setError('');
    setOpen(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Le nom est requis');
      return;
    }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, data: form });
      } else {
        await createMut.mutateAsync(form);
      }
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const handleDelete = async (site: Site) => {
    if (!window.confirm(`Désactiver le site "${site.name}" ?`)) return;
    try {
      await deleteMut.mutateAsync(site.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <div>
      <Header
        title="Sites"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Nouveau site
          </Button>
        }
      />

      {isLoading ? (
        <Card><p className="text-sm text-stone-400">Chargement...</p></Card>
      ) : !sites?.length ? (
        <Card>
          <div className="text-center py-8">
            <Building2 size={32} className="mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-500 mb-4">Aucun site configuré</p>
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} className="mr-1" /> Créer le premier site
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((site) => (
            <Card key={site.id} className="hover:border-accent/40 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-accent-soft flex items-center justify-center flex-shrink-0">
                    <Building2 size={18} className="text-accent" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-ink truncate">{site.name}</h3>
                    {site.address && (
                      <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{site.address}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => openEdit(site)}
                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-ink"
                    title="Modifier"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(site)}
                    className="p-1.5 rounded hover:bg-rose-50 text-stone-500 hover:text-rose-600"
                    title="Désactiver"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-stone-100 text-xs text-stone-600">
                <span className="flex items-center gap-1.5">
                  <Users size={12} className="text-stone-400" />
                  <span className="num font-semibold">{site.employees_count ?? 0}</span> employés
                </span>
                <span className="flex items-center gap-1.5">
                  <Radio size={12} className="text-stone-400" />
                  <span className="num font-semibold">{site.readers_count ?? 0}</span> bornes
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Modifier le site' : 'Nouveau site'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Nom"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Argenteuil"
            autoFocus
          />
          <Input
            label="Adresse (optionnelle)"
            value={form.address ?? ''}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="12 rue de la Gare, 95100 Argenteuil"
          />
          {error && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-md p-2">
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
